<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import UserStatus from "@/components/statuses/UserStatus.vue";
import UserCard from "@/components/user/UserCard.vue";
import UserTruck from "@/components/user/UserTruck.vue";
import { API } from "@/api/api";
import type { TruckDto } from "@/api/trucks/dto/truck.dto";
import type { OwnerCoordinatorResponse } from "@/api/users/dto/response/owner-coordinator.response";
import type { ChatInfoUser } from "@/types/chat";

const props = defineProps<{
  user: ChatInfoUser;
}>();

const { t } = useI18n();
const api = new API();

const truck = ref<TruckDto | null>(null);
const ownerCoordinators = ref<OwnerCoordinatorResponse[]>([]);

const uniqueCoordinators = computed(() => {
  const coordinators = [...props.user.coordinators, ...ownerCoordinators.value];
  const seen = new Set<number>();

  return coordinators.filter((coordinator) => {
    if (seen.has(coordinator.userId)) {
      return false;
    }

    seen.add(coordinator.userId);
    return true;
  });
});

function findDriverTruck(trucks: TruckDto[]): TruckDto | null {
  return (
    trucks.find((item) => item.driverId === props.user.id) ??
    trucks.find((item) => item.driver2Id === props.user.id) ??
    null
  );
}

async function loadData() {
  try {
    const response = await api.trucks.findTrucks({ driver: props.user.id });

    if (response.success && response.data) {
      truck.value = findDriverTruck(response.data.items);
    }

    if (props.user.owner?.id) {
      const resp = await api.users.findOwnerCoordinators(props.user.owner.id);

      if (resp.success && resp.data) {
        ownerCoordinators.value = resp.data;
      }
    }
  } catch (error) {
    console.error(error);
  }
}

watch(
  () => props.user,
  () => {
    loadData();
  },
);

onMounted(() => {
  loadData();
});
</script>

<template>
  <div v-if="user.isDriver" class="panel-info__block rounded" data-qa="driver-info-block">
    <div
      class="panel-info__block-header d-flex justify-space-between align-center bg-indigo-lighten-2 pt-2 pb-2 pl-5 pr-5 rounded-b-0 rounded"
    >
      <span class="font-size-12 font-weight-600 text-uppercase text-white">{{ t("drivers.driver") }}</span>
      <user-status :status="user.driverStatus" />
    </div>
    <div class="panel-info__block-content pa-3">
      <div v-if="truck" class="pa-3 bg-white rounded content mb-3">
        <user-truck :truck="truck" :user="user"></user-truck>
      </div>

      <user-card
        v-if="user.owner?.id && user?.id && user.owner.id !== user.id"
        :is-financially-responsible="user.ownerId === user.contactPersonId"
        :role="t('drivers.owner')"
        :status="user.owner.ownerStatus"
        :user="user.owner"
        role-color="text-green-lighten-1"
      />

      <user-card
        v-for="(coordinator, index) in uniqueCoordinators"
        :key="index"
        :full-access="coordinator.fullAccess"
        :is-financially-responsible="user.contactPersonId === coordinator.userId"
        :role="t('drivers.coordinator')"
        :user="coordinator.user"
        role-color="text-blue-lighten-2"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.panel-info__block {
  margin-bottom: 16px;
  background: #f8f9fa;
  border-bottom: none;
}

.panel-info__block-content {
  .content {
    box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.16);
  }
}

.driver-switch-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-contact__icon {
  width: 24px;
  flex: 0 0 24px;
  height: 24px;
  cursor: pointer;
}

.status-item {
  font-size: 9px;
  width: 17px;
  height: 17px;
}
</style>
